import { useProductCatalog } from '@hotjar/webapp/domains/common/product/hooks/useProductCatalog';

import type { AnalyticsProduct, FeedbackProduct } from '../types';
import { ProductType } from '../types';

type ObjectType<T> = T extends ProductType.Feedback
  ? FeedbackProduct
  : T extends ProductType.Analytics
  ? AnalyticsProduct
  : never;

interface UseProductComparatorsResult {
  productLt: (lhs: string, rhs: string) => boolean;
  bySku: <T extends ProductType>(sku: string, type: T) => ObjectType<T> | undefined;
}

export const useProductComparators = (): UseProductComparatorsResult => {
  const { data: productCatalog } = useProductCatalog();

  const bySku = <T extends ProductType>(sku: string, type: T): ObjectType<T> | undefined => {
    switch (type) {
      case ProductType.Analytics: {
        return productCatalog?.analyticsProducts.find((x) => x.sku === sku) as ObjectType<T>;
      }
      case ProductType.Feedback: {
        return productCatalog?.feedbackProducts.find((x) => x.sku === sku) as ObjectType<T>;
      }
      default: {
        return undefined;
      }
    }
  };

  return {
    /*
     * Check if a product is 'less than' the other product by comparing the products
     * dailyCcSessions (for analytics products) or monthlyResponses for feedback products
     */
    productLt: (lhs: string, rhs: string): boolean => {
      const lhsProduct = bySku(lhs, ProductType.Analytics) || bySku(lhs, ProductType.Feedback);
      const rhsProduct = bySku(rhs, ProductType.Analytics) || bySku(rhs, ProductType.Feedback);

      // Return false if we can't find the product by sku or if the two products are of different types
      if (!lhsProduct || !rhsProduct || lhsProduct.type !== rhsProduct?.type) return false;

      if (lhsProduct.type === ProductType.Analytics && rhsProduct.type === ProductType.Analytics) {
        return lhsProduct.dailyCcSessions < rhsProduct.dailyCcSessions;
      }

      if (lhsProduct.type === ProductType.Feedback && rhsProduct.type === ProductType.Feedback) {
        return lhsProduct.monthlyResponses < rhsProduct.monthlyResponses;
      }

      return false;
    },
    bySku,
  };
};
